import { Slot } from "radix-ui";
import type { ComponentPropsWithRef, ReactNode } from "react";

import { cx } from "@/lib/cx";

import styles from "./Button.module.css";
import { Spinner } from "./Spinner";
import { Tooltip } from "./Tooltip";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "xs" | "sm" | "md" | "lg";

export interface ButtonProps extends ComponentPropsWithRef<"button"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner, disables the button and sets aria-busy. */
  loading?: boolean;
  /** Icon before the label; decorative, the label carries the meaning. */
  icon?: ReactNode;
  /** Icon after the label, e.g. a chevron on a menu button. */
  trailingIcon?: ReactNode;
  /** Style the single child (e.g. a link) as a button instead of rendering a <button>. */
  asChild?: boolean;
}

export function buttonClass(
  variant: ButtonVariant,
  size: ButtonSize,
  ...extra: (string | undefined)[]
) {
  return cx(styles.button, styles[variant], styles[size], ...extra);
}

export function Button({
  variant = "secondary",
  size = "md",
  loading = false,
  icon,
  trailingIcon,
  asChild = false,
  disabled,
  className,
  children,
  type = "button",
  ...props
}: ButtonProps) {
  const classes = buttonClass(variant, size, className);
  const lead = loading ? <Spinner /> : icon;
  if (asChild) {
    return (
      <Slot.Root className={classes} {...props}>
        <Slot.Slottable>{children}</Slot.Slottable>
      </Slot.Root>
    );
  }
  return (
    <button
      type={type}
      className={classes}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {lead !== undefined && (
        <span className={styles.iconSlot} aria-hidden="true">
          {lead}
        </span>
      )}
      {children}
      {trailingIcon !== undefined && (
        <span className={styles.iconSlot} aria-hidden="true">
          {trailingIcon}
        </span>
      )}
    </button>
  );
}

export interface IconButtonProps extends Omit<
  ComponentPropsWithRef<"button">,
  "children" | "aria-label"
> {
  /** Required accessible name, also shown as the tooltip. */
  label: string;
  icon: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Pressed state for toggle buttons (sets aria-pressed). */
  pressed?: boolean;
  /** Show the label as a tooltip on hover and focus. */
  tooltip?: boolean;
  /** Extra tooltip text such as a shortcut, e.g. "Ctrl+K". */
  shortcut?: string;
}

/** Square icon-only button. The label is mandatory so it is never unnamed for screen readers. */
export function IconButton({
  label,
  icon,
  variant = "ghost",
  size = "md",
  pressed,
  tooltip = true,
  shortcut,
  className,
  type = "button",
  ...props
}: IconButtonProps) {
  const button = (
    <button
      type={type}
      aria-label={label}
      aria-pressed={pressed}
      className={buttonClass(
        variant,
        size,
        styles.square,
        pressed === true ? styles.pressed : undefined,
        className,
      )}
      {...props}
    >
      <span className={styles.iconSlot} aria-hidden="true">
        {icon}
      </span>
    </button>
  );
  if (!tooltip) return button;
  return (
    <Tooltip content={shortcut === undefined ? label : `${label} (${shortcut})`}>{button}</Tooltip>
  );
}
